import React, { SetStateAction, useEffect } from "react"
import { useTranslation } from "react-i18next"
import Stack from "@mui/material/Stack"
import {
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
  UseFormWatch
} from "react-hook-form"
import { FEMALE, MALE } from "constants/index"
import { useAppSelector } from "appRedux/hooks"
import TextField from "components/atoms/TextField"
import Select from "components/atoms/Select"
import Calendar from "components/atoms/Calendar"
import ProfileImageSelection from "components/molecules/ProfileImageSelection"

type UserProfileFormFieldsProps = {
  register: UseFormRegister<any>;
  watch: UseFormWatch<any>;
  reset: UseFormReset<any>;
  getValues: UseFormGetValues<any>;
  setValue: UseFormSetValue<any>;
  errors: FieldErrors<UserProfileUpdateRequestPayload>;
  setAvatarIsDirty: React.Dispatch<SetStateAction<boolean>>;
}

export default function UserProfileFormFields(props: UserProfileFormFieldsProps) {
  const {
    register,
    watch,
    reset,
    getValues,
    setValue,
    errors,
    setAvatarIsDirty
  } = props

  const userProfile = useAppSelector((state) => state.user.profile)

  const { t } = useTranslation()

  const birthday = watch("birthday")
  const gender = watch("gender")

  const genderOptions = [
    { value: FEMALE, label: t("user.female") },
    { value: MALE, label: t("user.male") }
  ]

  useEffect(() => {
    setDefaultFormValues()
  }, [])

  function setDefaultFormValues() {
    const prevValues: any = {}
    const defaultFormValues = getValues()
    if (!userProfile) return
    Object.entries(userProfile).map(([key, value]) => {
      if (defaultFormValues.hasOwnProperty(key)) {
        prevValues[key] = value || ""
      }
    })
    reset((formValues: UserProfileUpdateRequestPayload) => ({
      ...formValues,
      ...prevValues
    }))
  }

  function setAvatar(value: File | null) {
    setValue("avatar", value, { shouldDirty: true })
    setAvatarIsDirty(true)
  }

  function setBirthday(value: string) {
    setValue("birthday", value, { shouldDirty: true })
  }

  function setGender(value: GENDER) {
    setValue("gender", value, { shouldDirty: true })
  }

  return (
    <Stack width="100%" alignItems="center" marginBottom={0} flexGrow={1}>
      <ProfileImageSelection
        id="avatar"
        defaultValue={getValues("avatar")}
        register={register}
        setValue={setValue}
        onChange={setAvatar}
      />
      <TextField
        id="name"
        label={t("user.name")}
        defaultValue={getValues("name")}
        register={register}
        setValue={setValue}
        error={errors.name}
      />
      <TextField
        id="email"
        label={t("user.email")}
        type="email"
        required={true}
        defaultValue={getValues("email")}
        register={register}
        setValue={setValue}
        error={errors.email}
      />
      <TextField
        id="phoneNumber"
        label={t("user.phoneNumber")}
        type="text"
        pattern="[0-9]*"
        defaultValue={getValues("phoneNumber")}
        register={register}
        setValue={setValue}
        error={errors.phoneNumber}
        direction={"rtl"}
      />
      <Calendar
        id="birthday"
        label={t("user.birthday")}
        value={birthday}
        register={register}
        setValue={setValue}
        onChange={setBirthday}
      />
      <Select
        id="gender"
        label={t("user.gender")}
        options={genderOptions}
        value={gender}
        register={register}
        setValue={setValue}
        isDeletable={true}
        onChange={setGender}
      />
    </Stack>
  )
}
